/**
 * The toolbar's "add" button and its small menu of the six node kinds.
 *
 * Hand-rolled on purpose (@wa/widgets cannot use @wa/ui's popover): a button
 * with aria-haspopup / aria-expanded and a role="menu" list. Opening focuses the
 * first kind; the arrow keys, Home and End move between kinds; Enter or a click
 * adds one; Escape closes and returns focus to the button; Tab or a click
 * anywhere else closes it.
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactElement } from "react";
import { IconPlus, KindIcon } from "./icons.js";
import { FLOW_KIND_LABEL } from "./labels.js";
import { FLOW_NODE_KINDS, type FlowMapLabels, type FlowNodeKind } from "./model.js";

export function AddMenu({
  labels,
  onAdd,
  disabled,
}: {
  labels: FlowMapLabels;
  onAdd: (kind: FlowNodeKind) => void;
  disabled?: boolean;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
    const onPointerDown = (e: PointerEvent): void => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open]);

  const close = (refocus: boolean): void => {
    setOpen(false);
    if (refocus) button.current?.focus();
  };

  const onMenuKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    const items = Array.from(menu.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? []);
    if (!items.length) return;
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    const focus = (i: number): void => items[(i + items.length) % items.length]?.focus();
    switch (e.key) {
      case "Escape":
        e.preventDefault();
        // Keep the map (and the host) from treating this Escape as "clear the selection" or "close the dialog".
        e.stopPropagation();
        close(true);
        break;
      case "ArrowDown":
        e.preventDefault();
        focus(at + 1);
        break;
      case "ArrowUp":
        e.preventDefault();
        focus(at - 1);
        break;
      case "Home":
        e.preventDefault();
        focus(0);
        break;
      case "End":
        e.preventDefault();
        focus(items.length - 1);
        break;
      case "Tab":
        setOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <div className="wa-fm__menu-wrap" ref={wrap}>
      <button
        ref={button}
        type="button"
        className="wa-fm__btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        title={labels.add}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <IconPlus size={16} />
        <span className="wa-fm__btn-text">{labels.add}</span>
      </button>
      {open ? (
        <div ref={menu} id={menuId} role="menu" aria-label={labels.add} className="wa-fm__menu" onKeyDown={onMenuKeyDown}>
          {FLOW_NODE_KINDS.map((kind) => (
            <button
              key={kind}
              type="button"
              role="menuitem"
              tabIndex={-1}
              className={`wa-fm__menu-item wa-fm__menu-item--${kind}`}
              onClick={() => {
                close(false);
                onAdd(kind);
              }}
            >
              <KindIcon kind={kind} size={16} />
              <span>{labels[FLOW_KIND_LABEL[kind]]}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
